import * as React from 'react';
import { AppControlProps } from '../../../controls/AppControlProps';
import { AppButton } from '../../../controls/AppButton';
import { AppPersonaButton } from '../../../controls/AppPersonaButton/AppPersonaButton';
import { Icon } from '@fluentui/react/lib/Icon';
import { CoauthorInformation } from './CoauthorInformation';
import {
  getCoauthorSectionStyle,
  getOverflowMenuItemStyles,
  getPersonaCoinStyles,
  CoauthorGalleryPalette,
} from './Styling';
import { PersonaCoin } from '@fluentui/react/lib/Persona';
import { getInitials, BaseButton, IButtonStyles } from '@fluentui/react';
import { sortCoauthors } from './utilities';

/**
 * Function that will convert control props into controls that can be rendered within the action hub.
 * @param props the control props that will be converted to a JSX element
 */
export const collabControlRendererFunction = (styles?: IButtonStyles) => {
  return (props: AppControlProps) => {
    switch (props.type) {
      case 'AppButtonProps':
        return <AppButton styles={styles} role={'menuitem'} {...props} />;
      case 'AppPersonaButtonProps':
        return <AppPersonaButton styles={styles} role={'menuitem'} {...props} />;
      default: {
        throw new Error(`The control of type ${props.type} is not a valid collab control`);
      }
    }
  };
};

export function onRenderIcon(size: number, icon?: string) {
  return () => <Icon iconName={icon} style={{ width: size, height: size }} />;
}

/**
 * Function that can be used to render a list of coauthors.
 * @param coauthors list of coauthors to render
 */
export const onRenderCoauthorSection = (
  coauthors: CoauthorInformation[],
  palette: CoauthorGalleryPalette,
  fallBackUserImage?: string,
  onCoauthorMenuItemExecute?: (ev: React.MouseEvent<HTMLDivElement>, coauthor: CoauthorInformation) => void
): (() => JSX.Element) => {
  return () => (
    <div role="presentation" key={'coauthor-menu-coauthors'}>
      <ul className={getCoauthorSectionStyle(palette).container} role="group">
        {sortCoauthors(coauthors).map((coauthor, index, coauthors) =>
          getCoauthorSectionItem(coauthor, index, coauthors, palette, fallBackUserImage, onCoauthorMenuItemExecute)
        )}
      </ul>
    </div>
  );
};

/**
 * Function that will be used to render all of the coauthors as buttons with corresponding persona coins in the overflow menu
 * @param coauthor Coauthor that's information is being read from to create the section item
 */
export const getCoauthorSectionItem = (
  coauthor: CoauthorInformation,
  index: number,
  coauthors: CoauthorInformation[],
  palette: CoauthorGalleryPalette,
  fallBackUserImage?: string,
  onCoauthorMenuItemExecute?: (ev: React.MouseEvent<HTMLDivElement>, coauthor: CoauthorInformation) => void
): JSX.Element => {
  const coauthorMenuItemProps = {
    text: coauthor.name,
    id: `${coauthor.id}-MenuItem`,
    key: `coauthor-${index}`,
    secondaryText: coauthor.description,
    styles: getOverflowMenuItemStyles(palette),
    ariaSetsize: coauthors.length,
    ariaPosInSet: index + 1,
    role: 'menuitem',
    disabled: !coauthor.hasRing,
    onClick: (ev: React.MouseEvent<HTMLDivElement>) => {
      onCoauthorMenuItemExecute && onCoauthorMenuItemExecute(ev, coauthor);
    },
    onRenderIcon: () => (
      <PersonaCoin
        imageUrl={coauthor.imageUrl}
        imageShouldStartVisible={true}
        coinSize={palette.overflowCoinSize}
        imageInitials={getInitials(coauthor.name, false)}
        initialsColor={coauthor.color}
        showInitialsUntilImageLoads={true}
        onRenderInitials={
          getInitials(coauthor.name, false) === '' || coauthor.isAnonymous
            ? onRenderIcon(palette.overflowCoinSize, fallBackUserImage)
            : undefined
        }
        styles={{ coin: getPersonaCoinStyles(palette, coauthor.color, coauthor.hasRing) }}
      />
    ),
  };
  return (
    <li role="presentation" className={getCoauthorSectionStyle(palette).item} key={coauthorMenuItemProps.key}>
      <BaseButton {...coauthorMenuItemProps} />
    </li>
  );
};
